import { msg, t } from '@lingui/core/macro';
import { defineScreen, action } from '@/shared/screen/define';

// Segurança nunca é automatizada pelo agente (U24): `agent: false`, nenhuma ação `page: true`, e as
// ações com efeito só rodam pela tela montada. A troca de senha é credencial: nunca vira ferramenta.
const mountedScreenRequired = (): never => {
  throw new Error(t`Abra Meu perfil para executar esta ação.`);
};

export const PROFILE_PATH = '/meu-perfil';
export const NOTIFICATION_SETTINGS_PATH = '/meu-perfil/configuracoes';
export const EFFECTIVE_PERMISSIONS_PATH = '/meu-perfil/permissoes-efetivas';

export const profileOpenNotifications = action({
  id: 'profile.notifications.open',
  kind: 'navigate',
  label: msg`Configurações de notificações`,
  description: msg`Abre as configurações de notificações da pessoa conectada.`,
  run: ({ nav }) => nav.to(NOTIFICATION_SETTINGS_PATH),
});
export const profileOpenPermissions = action({
  id: 'profile.permissions.open',
  kind: 'navigate',
  label: msg`Permissões efetivas`,
  description: msg`Abre as permissões efetivas da pessoa conectada.`,
  run: ({ nav }) => nav.to(EFFECTIVE_PERMISSIONS_PATH),
});
export const profileSavePersonalData = action({
  id: 'profile.personalData.save',
  kind: 'write',
  label: msg`Configurações de dados pessoais`,
  description: msg`Salva os dados pessoais da pessoa conectada: nome, CPF, e-mail, telefone e endereço. Só pela tela, depois da confirmação.`,
  preview: () => ({ title: t`Configurações de dados pessoais`, detail: t`Configure suas informações pessoais` }),
  run: mountedScreenRequired,
});
export const profileChangePassword = action({
  id: 'profile.password.change',
  kind: 'write',
  label: msg`Alterar senha`,
  description: msg`Troca a senha da pessoa conectada. Só a própria pessoa faz, pela tela; nunca é automatizada.`,
  preview: () => ({ title: t`Alterar senha`, detail: t`Confirme as informações antes de alterar sua senha` }),
  run: mountedScreenRequired,
});
export const profileSignOut = action({
  id: 'profile.signOut',
  kind: 'destructive',
  label: msg`Sair da conta`,
  description: msg`Encerra a sessão neste dispositivo, depois da confirmação da pessoa.`,
  preview: () => ({ title: t`Sair da conta`, detail: t`Tem certeza que realmente deseja sair da conta?`, tone: 'destructive' }),
  run: mountedScreenRequired,
});
export const notificationSettingsSave = action({
  id: 'profile.notifications.save',
  kind: 'write',
  label: msg`Salvar configurações`,
  description: msg`Salva o nível escolhido para cada tipo de notificação da pessoa conectada.`,
  preview: () => ({ title: t`Salvar configurações` }),
  run: mountedScreenRequired,
});
export const effectivePermissionsRefresh = action({
  id: 'profile.permissions.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo as permissões efetivas da pessoa conectada. Não muda nada.`,
  run: mountedScreenRequired,
});

export const profileScreen = defineScreen({
  id: 'profile.me',
  path: PROFILE_PATH,
  title: msg`Meu perfil`,
  description: msg`Área da conta da pessoa conectada: cadastro, dados pessoais, senha, notificações e permissões.`,
  // Sem menu (`showNav: false`, router.tsx:329-334): mora sob o Início na trilha (U42).
  place: { icon: 'user', parent: 'home' },
  agent: false,
  actions: [profileOpenNotifications, profileOpenPermissions, profileSavePersonalData, profileChangePassword, profileSignOut],
});
export const notificationSettingsScreen = defineScreen({
  id: 'profile.notificationSettings',
  path: NOTIFICATION_SETTINGS_PATH,
  title: msg`Notificações`,
  description: msg`Nível de notificação (nenhum, só os meus, meus grupos ou todos) de cada tipo de notificação da pessoa conectada.`,
  place: { icon: 'bell', parent: 'profile.me' },
  agent: false,
  actions: [notificationSettingsSave],
});
export const effectivePermissionsScreen = defineScreen({
  id: 'profile.effectivePermissions',
  path: EFFECTIVE_PERMISSIONS_PATH,
  title: msg`Permissões efetivas`,
  description: msg`O que a pessoa conectada pode ver e fazer no sistema, área por área.`,
  place: { icon: 'shield-check', parent: 'profile.me' },
  agent: false,
  actions: [effectivePermissionsRefresh],
});
